import { useId, useState, type KeyboardEvent, type ReactNode } from 'react';
import { NJAvatar, NJAvatarList, NJButton, NJFormItem, NJIcon, NJIconButton, NJMenu, NJTag, NJText, NJToastService, NJTooltip } from '@engie-group/fluid-design-system-react';
import { BROAD_KINDS, GRANT_ROLES, KIND_ICON, KIND_LABEL, ROLE_LABEL, USERS } from './data';
import { memberCount } from './permissions';
import type { AccessEntry, GrantRole, NewAoTeam, Principal } from './types';

export function successToast(message: string) {
    NJToastService(message, { iconName: 'check_circle', dismissAfter: 4000, hasCloseIcon: true });
}

export function errorToast(message: string) {
    NJToastService(message, { title: 'Action impossible', iconName: 'error', dismissAfter: 6000, hasCloseIcon: true });
}

/** Runs a mutation and reports the outcome; returns true on success. */
export async function run(action: () => Promise<unknown>, message: string): Promise<boolean> {
    try {
        await action();
        successToast(message);
        return true;
    } catch (error) {
        errorToast(error instanceof Error ? error.message : 'Erreur inattendue');
        return false;
    }
}

export function PrincipalAvatar({ principal, scale = 'md' }: { principal: Principal; scale?: 'sm' | 'md' | 'lg' }) {
    if (principal.kind === 'user') {
        return <NJAvatar.Root className={`ns-tone-${principal.tone}`} scale={scale} initials={principal.initials} label={principal.name} />;
    }
    return (
        <span className={`ns-group-avatar ns-group-avatar--${scale} ns-tone-${principal.tone}`} aria-hidden="true">
            <NJIcon name={KIND_ICON[principal.kind]} size="sm" />
        </span>
    );
}

function groupDetail(principal: Principal) {
    const count = memberCount(principal);
    return principal.kind === 'user' ? principal.detail : `${principal.detail} · ${count} membre${count > 1 ? 's' : ''}`;
}

/** Figma-style overlapping avatar stack of everyone who has access. */
export function AvatarStack({ entries, onClick, active }: { entries: AccessEntry[]; onClick: () => void; active?: boolean }) {
    const label = entries.length === 1 ? 'Privé — seul le propriétaire a accès' : `${entries.length} accès — voir qui a accès`;
    return (
        <button type="button" className={`ns-stack ${active ? 'ns-stack--active' : ''}`} onClick={onClick} aria-label={label} aria-expanded={active}>
            <NJAvatarList max={3} size="md" showMoreLabel={(count) => `${count} accès supplémentaires`}>
                {entries.map(({ principal }) => (
                    <NJTooltip key={principal.id} text={principal.name}>
                        {principal.kind === 'user' ? (
                            <NJAvatar.Root className={`ns-tone-${principal.tone}`} scale="md" initials={principal.initials} label={principal.name} />
                        ) : (
                            <NJAvatar.Root className={`ns-tone-${principal.tone} ns-avatar--group`} scale="md" label={principal.name}>
                                <NJIcon name={KIND_ICON[principal.kind]} size="sm" />
                            </NJAvatar.Root>
                        )}
                    </NJTooltip>
                ))}
            </NJAvatarList>
            {entries.length === 1 && <NJIcon name="lock" size="sm" className="ns-stack__lock" />}
        </button>
    );
}

/** Role dropdown (Figma / Drive style). `onRemove` adds the « Retirer l'accès » action. */
export function RoleMenu({ role, onChange, onRemove, onTransfer, outlined, label }: { role: GrantRole; onChange: (role: GrantRole) => void; onRemove?: () => void; onTransfer?: () => void; outlined?: boolean; label: string }) {
    return (
        <NJMenu.Root>
            <NJMenu.Anchor>
                <button type="button" className={`ns-select ${outlined ? 'ns-select--outlined' : ''}`} aria-label={`${label} : ${ROLE_LABEL[role]}`}>
                    <NJText scale="sm">{ROLE_LABEL[role]}</NJText>
                    <NJIcon name="expand_more" size="sm" />
                </button>
            </NJMenu.Anchor>
            <NJMenu.Dropdown>
                <NJMenu.Group>
                    {GRANT_ROLES.map((r) => (
                        <NJMenu.Item key={r} label={ROLE_LABEL[r]} selected={r === role} leading={r === role ? <NJIcon name="check" size="sm" /> : <span className="ns-menu-spacer" aria-hidden="true" />} onClick={() => onChange(r)}>
                            {ROLE_LABEL[r]}
                        </NJMenu.Item>
                    ))}
                </NJMenu.Group>
                {(onRemove || onTransfer) && (
                    <NJMenu.Group>
                        {onTransfer && <NJMenu.Item label="Transférer la propriété" leading={<NJIcon name="swap_horiz" size="sm" />} onClick={onTransfer}>Transférer la propriété</NJMenu.Item>}
                        {onRemove && <NJMenu.Item label="Retirer l’accès" leading={<NJIcon name="person_remove" size="sm" />} onClick={onRemove}>Retirer l’accès</NJMenu.Item>}
                    </NJMenu.Group>
                )}
            </NJMenu.Dropdown>
        </NJMenu.Root>
    );
}

export function AccessRow({ entry, canManage, isMe, onChange, onRemove, onTransfer }: {
    entry: AccessEntry;
    canManage: boolean;
    isMe?: boolean;
    onChange: (role: GrantRole) => void;
    onRemove: () => void;
    /** Set only for users when the viewer owns the network. */
    onTransfer?: () => void;
}) {
    const { principal, role } = entry;
    return (
        <li className="ns-row">
            <PrincipalAvatar principal={principal} />
            <div className="ns-row__text">
                <div className="ns-row__title">
                    <NJText scale="sm" className="ns-strong">{principal.name}{isMe ? ' (vous)' : ''}</NJText>
                </div>
                <NJText scale="xs" variant="secondary">{groupDetail(principal)}</NJText>
            </div>
            {role === 'owner' || !canManage
                ? <NJText scale="sm" variant="secondary" className="ns-role-static">{ROLE_LABEL[role]}</NJText>
                : <RoleMenu role={role} label={`Droit de ${principal.name}`} onChange={onChange} onRemove={onRemove} onTransfer={onTransfer} />}
        </li>
    );
}

function normalize(value: string) {
    return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

/** One field for people, AO teams and every org level, with keyboard navigation. */
export function PrincipalSearch({ options, onPick, placeholder, chips, autoFocus, footer }: {
    options: Principal[];
    onPick: (principal: Principal) => void;
    placeholder: string;
    chips?: ReactNode;
    autoFocus?: boolean;
    footer?: ReactNode;
}) {
    const listId = useId();
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [cursor, setCursor] = useState(0);
    const q = normalize(query.trim());
    const words = q.split(/\s+/).filter(Boolean);
    const results = options.filter((p) => {
        const haystack = normalize(`${p.name} ${p.detail} ${KIND_LABEL[p.kind]}`);
        return words.every((word) => haystack.includes(word));
    }).slice(0, 6);

    const pick = (principal: Principal) => {
        onPick(principal);
        setQuery('');
        setCursor(0);
        setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'ArrowDown') { event.preventDefault(); setOpen(true); setCursor((c) => Math.min(c + 1, results.length - 1)); }
        if (event.key === 'ArrowUp') { event.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
        if (event.key === 'Enter' && results[cursor]) { event.preventDefault(); pick(results[cursor]); }
        if (event.key === 'Escape' && open) { event.preventDefault(); setOpen(false); }
    };

    return (
        <div className="ns-search">
            <div className="ns-field">
                <NJIcon name="person_add" size="sm" />
                {chips}
                <input
                    className="ns-field__input"
                    value={query}
                    placeholder={placeholder}
                    aria-label={placeholder}
                    role="combobox"
                    aria-expanded={open}
                    aria-controls={listId}
                    aria-activedescendant={open && results[cursor] ? `${listId}-${results[cursor].id}` : undefined}
                    autoFocus={autoFocus}
                    onChange={(e) => { setQuery(e.target.value); setOpen(true); setCursor(0); }}
                    onClick={() => setOpen(true)}
                    onBlur={() => setOpen(false)}
                    onKeyDown={onKeyDown}
                />
            </div>
            {open && (
                <div className="ns-suggest" role="listbox" id={listId} aria-label="Suggestions" onMouseDown={(e) => e.preventDefault()}>
                    {results.length === 0 && <NJText scale="sm" variant="secondary" className="ns-suggest__empty">Aucun résultat pour « {query} »</NJText>}
                    {results.map((principal, index) => (
                        <div
                            key={principal.id}
                            id={`${listId}-${principal.id}`}
                            className="ns-suggest__item"
                            role="option"
                            aria-selected={index === cursor}
                            onMouseEnter={() => setCursor(index)}
                            onClick={() => pick(principal)}
                        >
                            <PrincipalAvatar principal={principal} scale="sm" />
                            <div className="ns-row__text">
                                <NJText scale="sm" className="ns-strong">{principal.name}</NJText>
                                <NJText scale="xs" variant="secondary">{principal.kind === 'user' ? principal.detail : `${KIND_LABEL[principal.kind]} · ${memberCount(principal)} membres`}</NJText>
                            </div>
                        </div>
                    ))}
                    {footer}
                </div>
            )}
        </div>
    );
}

export function CreateTeamOption({ onClick }: { onClick: () => void }) {
    return (
        <button type="button" className="ns-suggest__create" onClick={onClick}>
            <span className="ns-group-avatar ns-group-avatar--sm ns-tone-orange" aria-hidden="true"><NJIcon name="add" size="sm" /></span>
            <NJText scale="sm" className="ns-strong">Créer une équipe appel d’offres…</NJText>
        </button>
    );
}

/** Explains who gets access when groups are selected; warns for a territory or the GBU. */
export function ReachNotice({ principals }: { principals: Principal[] }) {
    const groups = principals.filter((p) => p.kind !== 'user');
    if (!groups.length) return null;
    const broad = groups.some((g) => BROAD_KINDS.includes(g.kind));
    const total = groups.reduce((sum, g) => sum + memberCount(g), 0);
    return (
        <div className={`ns-notice ${broad ? 'ns-notice--warning' : ''}`} role="status">
            <NJIcon name={broad ? 'warning' : 'info'} size="sm" />
            <NJText scale="xs">
                {broad ? 'Partage large : ' : ''}tous les membres de {groups.map((g) => g.name).join(', ')} auront accès, sous-niveaux inclus (environ {total} personnes).
            </NJText>
        </div>
    );
}

export function GeneralAccess({ shared }: { shared: boolean }) {
    return (
        <div className="ns-general-access">
            <span className="ns-group-avatar ns-group-avatar--md ns-tone-grey" aria-hidden="true"><NJIcon name={shared ? 'group' : 'lock'} size="sm" /></span>
            <div className="ns-row__text">
                <NJText scale="sm" className="ns-strong">{shared ? 'Accès restreint' : 'Privé'}</NJText>
                <NJText scale="xs" variant="secondary" className="ns-wrap">
                    {shared ? 'Seuls les personnes et groupes ci-dessus peuvent ouvrir ce réseau.' : 'Seule la propriétaire peut ouvrir ce réseau.'}
                </NJText>
            </div>
        </div>
    );
}

/** Sub-view to create a reusable, cross-team AO team and share the network with it. */
export function TeamCreator({ onBack, onCreate, pending }: { onBack: () => void; onCreate: (team: NewAoTeam) => Promise<boolean>; pending: boolean }) {
    const [name, setName] = useState('');
    const [memberIds, setMemberIds] = useState<string[]>([]);
    const [role, setRole] = useState<GrantRole>('write');
    const members = USERS.filter((u) => memberIds.includes(u.id));
    const ready = name.trim().length > 0 && memberIds.length > 0;

    return (
        <div className="ns-team">
            <div className="ns-team__head">
                <NJIconButton icon="arrow_back" label="Retour au partage" variant="tertiary" size="sm" onClick={onBack} />
                <div>
                    <NJText scale="md" className="ns-strong">Nouvelle équipe appel d’offres</NJText>
                    <NJText scale="xs" variant="secondary">Transversale et réutilisable sur vos autres réseaux.</NJText>
                </div>
            </div>
            <NJFormItem
                id="ns-team-name"
                label="Nom de l’équipe"
                size="sm"
                value={name}
                placeholder="Ex. AO Réseau des Tilleuls"
                onChange={(e) => setName((e.target as HTMLInputElement).value)}
            />
            <PrincipalSearch
                options={USERS.filter((u) => !memberIds.includes(u.id))}
                placeholder="Ajouter un membre par nom ou équipe…"
                onPick={(p) => setMemberIds((ids) => [...ids, p.id])}
            />
            {members.length > 0 ? (
                <ul className="ns-list" aria-label="Membres de l’équipe">
                    {members.map((principal) => (
                        <li key={principal.id} className="ns-row">
                            <PrincipalAvatar principal={principal} />
                            <div className="ns-row__text">
                                <NJText scale="sm" className="ns-strong">{principal.name}</NJText>
                                <NJText scale="xs" variant="secondary">{principal.detail}</NJText>
                            </div>
                            <NJIconButton icon="close" label={`Retirer ${principal.name}`} variant="tertiary" size="sm" onClick={() => setMemberIds((ids) => ids.filter((id) => id !== principal.id))} />
                        </li>
                    ))}
                </ul>
            ) : (
                <NJText scale="sm" variant="secondary">Aucun membre. Ajoutez les 2 ou 3 personnes de l’équipe AO, quelle que soit leur équipe.</NJText>
            )}
            <div className="ns-team__grant">
                <NJText scale="sm">Droit de l’équipe sur ce réseau</NJText>
                <RoleMenu role={role} label="Droit de l’équipe" outlined onChange={setRole} />
            </div>
            <div className="ns-footer">
                <NJButton label="Annuler" variant="secondary" emphasis="minimal" onClick={onBack} />
                <NJButton
                    label="Créer et partager"
                    icon="group_add"
                    disabled={!ready || pending}
                    onClick={async () => { if (await onCreate({ name: name.trim(), memberIds, role })) onBack(); }}
                />
            </div>
        </div>
    );
}

export function SelectedChips({ principals, onRemove }: { principals: Principal[]; onRemove: (id: string) => void }) {
    return (
        <>
            {principals.map((p) => (
                <NJTag key={p.id} label={p.name} variant={p.kind === 'user' ? 'blue' : 'orange'} size="sm" hasClose closeAriaLabel={`Retirer ${p.name}`} onClose={() => onRemove(p.id)} />
            ))}
        </>
    );
}

/** Sub-view to hand the network over to another user; the former owner keeps « Peut modifier ». */
export function TransferOwner({ ownerName, initial, options, onBack, onConfirm, pending }: {
    ownerName: string;
    initial: Principal | null;
    options: Principal[];
    onBack: () => void;
    onConfirm: (user: Principal) => Promise<boolean>;
    pending: boolean;
}) {
    const [target, setTarget] = useState<Principal | null>(initial);
    return (
        <div className="ns-team">
            <div className="ns-team__head">
                <NJIconButton icon="arrow_back" label="Retour au partage" variant="tertiary" size="sm" onClick={onBack} />
                <div>
                    <NJText scale="md" className="ns-strong">Transférer la propriété</NJText>
                    <NJText scale="xs" variant="secondary">Le réseau a un seul propriétaire, toujours une personne.</NJText>
                </div>
            </div>
            {target ? (
                <div className="ns-row ns-transfer-target">
                    <PrincipalAvatar principal={target} />
                    <div className="ns-row__text">
                        <NJText scale="sm" className="ns-strong">{target.name}</NJText>
                        <NJText scale="xs" variant="secondary">{target.detail}</NJText>
                    </div>
                    <NJIconButton icon="close" label={`Choisir une autre personne que ${target.name}`} variant="tertiary" size="sm" onClick={() => setTarget(null)} />
                </div>
            ) : (
                <PrincipalSearch options={options} placeholder="Nouveau propriétaire : nom ou équipe…" autoFocus onPick={setTarget} />
            )}
            <div className="ns-notice ns-notice--warning" role="status">
                <NJIcon name="warning" size="sm" />
                <NJText scale="xs">
                    {target ? `${target.name} deviendra propriétaire` : 'Le nouveau propriétaire'} : il pourra gérer les accès, transférer à nouveau ou supprimer le réseau.
                    {' '}Vous ({ownerName}) garderez le droit « Peut modifier », mais ne pourrez plus reprendre la propriété vous-même.
                </NJText>
            </div>
            <div className="ns-footer">
                <NJButton label="Annuler" variant="secondary" emphasis="minimal" onClick={onBack} />
                <NJButton
                    label="Transférer"
                    icon="swap_horiz"
                    disabled={!target || pending}
                    onClick={async () => { if (target && await onConfirm(target)) onBack(); }}
                />
            </div>
        </div>
    );
}
